import { FC, MouseEvent, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import isNumber from 'lodash/isNumber'
import { FormInput } from '@microservices/wiskey-react-components'
import {
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Done as DoneIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import { Box, Grid, IconButton } from '@mui/material'

import { ModalRefPathPicker } from '@pages/GanttChart/components/GanttCreateOrEdit/components/ModalRefPathPicker'

import { FIELD_BIND_VALUE_TYPE_OPTIONS } from '@components/CommandParameters/CommandParameters'

import { FIELD_BIND_TYPE_ENUM, GENERATOR_INPUT_TYPE, MAX_INPUT_LENGTH } from '@constants'
import { AutocompleteOption, CommandParametersType } from '@types'

type ParameterListItemType = {
  item: CommandParametersType
  itemIndex: number
  defaultFieldsObjectOption: AutocompleteOption<string> | null
  canChangeRefObject: boolean
  commandParameterOptions: AutocompleteOption[]
  refObjectOptions?: AutocompleteOption[]
  isLoadingCommandParameters: boolean
  editItemIndex: number | null
  onShowScriptValueDialog: () => void
  onSetCurrentNameScriptDialog: (value: 'value' | `parameters.${number}.value` | null) => void
  onClear: (event: MouseEvent<HTMLButtonElement>, index: number) => void
  onChangeType: (type: AutocompleteOption, index: number) => void
  onChangeObjectCode: (type: AutocompleteOption<string>, index: number) => void
  onDelete: (index: number) => void
  onEdit: (index: number | null) => void
  onSave: (index: number) => void
}

export const ParameterListItem: FC<ParameterListItemType> = ({
  item,
  itemIndex,
  defaultFieldsObjectOption,
  canChangeRefObject,
  commandParameterOptions,
  refObjectOptions,
  isLoadingCommandParameters,
  editItemIndex,
  onShowScriptValueDialog,
  onSetCurrentNameScriptDialog,
  onChangeType,
  onChangeObjectCode,
  onClear,
  onDelete,
  onEdit,
  onSave,
}) => {
  const { t } = useTranslation()
  const { watch, handleSubmit } = useFormContext()
  const watchType = watch(`parameters.${itemIndex}.type`)
  const watchObjectCode = watch(`parameters.${itemIndex}.objectCode`)

  const [isEdit, setEdit] = useState(false)

  const handleDelete = () => onDelete(itemIndex)
  const handleChangeType = (value: AutocompleteOption) => onChangeType(value, itemIndex)
  const handleChangeObjectCode = (value: AutocompleteOption<string>) =>
    onChangeObjectCode(value, itemIndex)
  const handleEdit = () => {
    setEdit(true)
    onEdit(itemIndex)
  }
  const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
    setEdit(false)
    onClear(event, itemIndex)
    onEdit(null)
  }
  const handleSave = () => {
    onSave(itemIndex)
    setEdit(false)
    onEdit(null)
  }

  return (
    <Grid container item alignItems='center' flexWrap='nowrap' sx={{ marginY: 0.5, height: 40 }}>
      <Grid container flexWrap='nowrap' height={40} xs={12}>
        {isEdit ? (
          <>
            <Grid item xs={3.5}>
              <FormInput
                autocompleteOptions={commandParameterOptions}
                disableClearable={true}
                formLabelSx={{ ml: '100px' }}
                inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
                loading={isLoadingCommandParameters}
                name={`parameters.${itemIndex}.title`}
                placeholder={t('commandParameters.inputs.parameter.placeholder')}
                rules={{ required: true }}
                formInputContainerSx={{
                  mb: 0,
                }}
                renderAutocompleteOption={(props, option) => (
                  <li {...props} key={option.id} title={option.label}>
                    {option.label}
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={2.5}>
              <FormInput
                autocompleteOptions={FIELD_BIND_VALUE_TYPE_OPTIONS}
                disableClearable={true}
                formLabelSx={{ ml: '68px' }}
                inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
                name={`parameters.${itemIndex}.type`}
                placeholder={t('commandParameters.inputs.type.placeholder')}
                rules={{
                  required: true,
                  maxLength: MAX_INPUT_LENGTH,
                }}
                onChangeAutocomplete={handleChangeType}
              />
            </Grid>
            <Grid item xs={4.5}>
              {watchType?.id !== FIELD_BIND_TYPE_ENUM.FIELD ? (
                <FormInput
                  autocompleteOptions={FIELD_BIND_VALUE_TYPE_OPTIONS}
                  disableClearable={true}
                  disabled={!watchType?.id}
                  formLabelSx={{ ml: '68px' }}
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  name={`parameters.${itemIndex}.value`}
                  placeholder={t('commandParameters.inputs.value.placeholder')}
                  readOnly={watchType?.id === FIELD_BIND_TYPE_ENUM.JS}
                  rules={{ required: true }}
                  additionalBtn={{
                    isEnabled: watchType?.id === FIELD_BIND_TYPE_ENUM.JS,
                    text: 'edit',
                    color: 'primary',
                    variant: 'contained',
                    onClick: () => {
                      onShowScriptValueDialog()
                      onSetCurrentNameScriptDialog(`parameters.${itemIndex}.value`)
                    },
                  }}
                  formInputContainerSx={{
                    flexWrap: 'nowrap',
                    '& .MuiButton-root': {
                      ml: 0.5,
                    },
                  }}
                />
              ) : (
                <ModalRefPathPicker
                  hasClearInInput
                  isRequired
                  embeddedObjectPickerControllerName={`parameters.${itemIndex}.pathArray`}
                  isDisabled={false}
                  name={`parameters.${itemIndex}.value`}
                  object={watchObjectCode || defaultFieldsObjectOption}
                  pickerName={`parameters.${itemIndex}.value`}
                  refObjectOptions={refObjectOptions}
                  refPickAllowed={true}
                  sx={{
                    ml: 1,
                    '& .MuiGrid-root': {
                      mb: 0,
                    },
                    '& .MuiBox-root': {
                      mb: 0,
                    },
                    '& .MuiTypography-root': {
                      width: 60,
                      minWidth: 60,
                    },
                    '& .MuiButton-root': {
                      margin: 0,
                      width: 64,
                    },
                  }}
                  onChangeRootObject={canChangeRefObject ? handleChangeObjectCode : undefined}
                />
              )}
            </Grid>
          </>
        ) : (
          <>
            <Grid
              item
              xs={3.5}
              sx={{
                display: 'flex',
                alignItems: 'center',
                pl: '100px',
                height: 40,
              }}
            >
              {item.title?.label ?? ''}
            </Grid>
            <Grid
              item
              xs={2.5}
              sx={{
                display: 'flex',
                alignItems: 'center',
                pl: '68px',
                height: 40,
              }}
            >
              {item.type?.label ?? ''}
            </Grid>
            <Grid
              item
              xs={4.5}
              sx={{
                display: 'flex',
                alignItems: 'center',
                pl: '68px',
                height: 40,
              }}
            >
              <Grid sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                {item.value}
              </Grid>
            </Grid>
          </>
        )}
        <Grid
          item
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          xs={1.5}
        >
          {isEdit ? (
            <Box>
              <IconButton
                disabled={isNumber(editItemIndex) && editItemIndex !== itemIndex}
                size='medium'
                sx={{ width: 40 }}
                onClick={handleSubmit(handleSave)}
              >
                <DoneIcon />
              </IconButton>
              <IconButton size='medium' sx={{ width: 40 }} onClick={handleClear}>
                <ClearIcon />
              </IconButton>
            </Box>
          ) : (
            <Box>
              <IconButton
                disabled={isNumber(editItemIndex) && editItemIndex !== itemIndex}
                size='medium'
                sx={{ width: 40 }}
                onClick={handleEdit}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                disabled={isNumber(editItemIndex) && editItemIndex !== itemIndex}
                size='medium'
                sx={{ width: 40 }}
                onClick={handleDelete}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}
